<template>
	<v-select ref="selectRef" :label="props.label" :items="sortedItems" :item-title="!props.titleKeyTranslate ? props.titleKey : (item) => t(item[props.titleKey])" item-value="id" v-model="selectItems" :menu-props="menuProps" :clearable="resolvedClearable" :multiple="props.multiple" :rules="validationRules" :density="props.density" variant="outlined" @update:menu="isMenuOpen = $event">
		<template v-if="sortedItems.length > 4" #menu-header="{ search, filteredItems: filtered }">
			<div class="pa-2">
				<v-text-field v-model="search.value" :error="!!search.value && !filtered.length" :label="t('Search')" density="compact" variant="outlined" prepend-inner-icon="search" clearable hide-details></v-text-field>
			</div>
			<v-list-item @click="toggle" v-if="props.multiple">
				<template #prepend>
					<v-checkbox-btn :indeterminate="selectlikesSomeItems && !likesAllItems" :model-value="likesAllItems"></v-checkbox-btn>
					<span class="ml-2">{{ likesAllItems ? t('DeselectAll') : t('SelectAll') }}</span>
				</template>
			</v-list-item>
			<v-divider v-if="props.multiple"></v-divider>
		</template>
		<template v-if="props.multiple" v-slot:selection="{ item, index }">
			<v-chip v-if="index < 2" :text="props.titleKeyTranslate ? t(item[props.titleKey]) : item[props.titleKey]"></v-chip>
			<span v-if="index === 2">(+{{ selectItems.length - 2 }})</span>
		</template>
	</v-select>
</template>

<script setup>
// #region Imports

import { shallowRef, inject, computed, ref, watch, watchEffect } from 'vue';
import { useTranslator } from '@/utils/useTranslator';
import { overlayHandler } from '@/utils/overlayHandler';

// #endregion

// #region Refs & Vars

const props = defineProps({
	label: { type: String, default: '' },
	items: { type: Array, default: () => [] },
	titleKey: { type: String, default: 'name' },
	titleKeyTranslate: { type: Boolean, default: false },
	multiple: { type: Boolean, default: false },
	required: { type: Boolean, default: false },
	clearable: { type: Boolean, default: null },
	sortItems: { type: Boolean, default: true },
	modelValue: { type: [Array, String, Number, null], default: undefined },
	preSelected: { type: [Array, String, Number, null], default: null },
	density: { type: String, default: 'compact' },
});
const emit = defineEmits(['update:modelValue', 'change']);

const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);
const isMenuOpen = ref(false);
const selectRef = ref(null);
const selectItems = shallowRef(props.multiple ? [] : null);
const menuProps = computed(() => {
	return {
		attach: overlayTarget || undefined,
		scrim: true,
		scrollStrategy: 'close',
	};
});
const validationRules = computed(() => {
	if (!props.required) return [];
	return [
		(v) => {
			if (props.multiple) return (Array.isArray(v) && v.length > 0) || t('FieldRequired');
			return (v !== null && v !== undefined && v !== '') || t('FieldRequired');
		},
	];
});
const sortedItems = computed(() => {
	if (!props.sortItems) {
		return [...props.items];
	}

	return [...props.items].sort((a, b) => {
		const titleA = props.titleKeyTranslate ? t(a[props.titleKey]) : a[props.titleKey];
		const titleB = props.titleKeyTranslate ? t(b[props.titleKey]) : b[props.titleKey];
		return String(titleA).localeCompare(String(titleB));
	});
});
const resolvedClearable = computed(() => (typeof props.clearable === 'boolean' ? props.clearable : !props.required));
const filterIsDirty = computed(() => {
	if (props.multiple) return selectItems.value.length > 0 && selectItems.value.length !== props.items.length;
	return selectItems.value !== null && selectItems.value !== undefined;
});

watch(isMenuOpen, () => {
	overlayHandler(selectRef);
});

watch(
	() => props.preSelected,
	(val) => {
		if (props.modelValue !== undefined) {
			return;
		}

		selectItems.value = props.multiple ? (Array.isArray(val) ? val : []) : val;
	}
);

watch(
	() => props.modelValue,
	(val) => {
		if (val === undefined) {
			return;
		}

		selectItems.value = props.multiple ? (Array.isArray(val) ? val : []) : val;
	},
	{ immediate: true }
);

// #endregion

// #region Filters Data

selectItems.value = props.multiple ? (Array.isArray(props.preSelected) ? props.preSelected : []) : props.preSelected;

watch(
	() => selectItems.value,
	(val) => {
		emit('update:modelValue', val);
		emit('change', val);
	},
	{ deep: true }
);

const likesAllItems = computed(() => {
	return selectItems.value.length === props.items.length;
});

const selectlikesSomeItems = computed(() => {
	return selectItems.value.length > 0;
});

// #endregion

// #region Methods

function toggle() {
	if (selectItems.value.length === sortedItems.value.length) {
		selectItems.value = [];
	} else {
		selectItems.value = sortedItems.value.map((t) => t.id);
	}
}

defineExpose({
	getValues() {
		if (props.multiple) return selectItems.value.length > 0 && selectItems.value.length != props.items.length ? selectItems.value : null;
		return selectItems.value ?? null;
	},
	getFullValues() {
		if (props.multiple) return props.items.filter((item) => selectItems.value.includes(item.id));
		return props.items.find((item) => item.id === selectItems.value) ?? null;
	},
	isDirty() {
		return filterIsDirty.value;
	},
	isValid() {
		if (!props.required) return true;
		if (props.multiple) return Array.isArray(selectItems.value) && selectItems.value.length > 0;
		return selectItems.value !== null && selectItems.value !== undefined && selectItems.value !== '';
	},
	isRequired() {
		return props.required;
	},
	selectItems(items) {
		selectItems.value = items;
	},
	reset() {
		selectItems.value = props.multiple ? [] : null;
	},
});

// #endregion
</script>
